import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Home, CheckSquare, Square, Star, Trophy, 
  Calendar, Clock, Sparkles
} from 'lucide-react';

const checklistCategories = [
  {
    id: 'documents',
    title: 'Belgeler',
    emoji: '📄',
    color: 'from-blue-500 to-indigo-600',
    items: [
      { id: 'd1', text: 'CV güncel ve tek sayfa', priority: 'high' },
      { id: 'd2', text: 'Profesyonel vesikalık fotoğraf hazır', priority: 'high' },
      { id: 'd3', text: 'Kimlik/Pasaport fotokopisi', priority: 'high' },
      { id: 'd4', text: 'Diploma/Transkript fotokopisi', priority: 'medium' },
      { id: 'd5', text: 'İngilizce sertifikası (varsa)', priority: 'medium' },
      { id: 'd6', text: 'Sağlık raporu (istenirse)', priority: 'low' },
      { id: 'd7', text: 'Referans mektupları', priority: 'low' }
    ]
  },
  {
    id: 'appearance',
    title: 'Görünüm',
    emoji: '👔',
    color: 'from-purple-500 to-pink-600',
    items: [
      { id: 'a1', text: 'Takım elbise/Kıyafet hazır ve ütülü', priority: 'high' },
      { id: 'a2', text: 'Ayakkabılar temiz ve cilalı', priority: 'high' },
      { id: 'a3', text: 'Saç kesimi/bakımı yapıldı', priority: 'high' },
      { id: 'a4', text: 'Tırnaklar düzgün kesildi', priority: 'medium' },
      { id: 'a5', text: 'Makyaj malzemeleri hazır (kadınlar)', priority: 'medium' },
      { id: 'a6', text: 'Kravat/Aksesuar seçildi', priority: 'medium' },
      { id: 'a7', text: 'Yedek çorap/kıyafet hazır', priority: 'low' }
    ]
  },
  {
    id: 'knowledge',
    title: 'Bilgi',
    emoji: '📚',
    color: 'from-emerald-500 to-teal-600',
    items: [
      { id: 'k1', text: 'Havayolu hakkında araştırma yapıldı', priority: 'high' },
      { id: 'k2', text: 'Temel mülakat soruları çalışıldı', priority: 'high' },
      { id: 'k3', text: 'STAR tekniği öğrenildi', priority: 'high' },
      { id: 'k4', text: 'Havayolunun destinasyonları bilinuyor', priority: 'medium' },
      { id: 'k5', text: 'Güncel havacılık haberleri okundu', priority: 'medium' },
      { id: 'k6', text: 'Güvenlik prosedürleri tekrar edildi', priority: 'medium' },
      { id: 'k7', text: '"Neden bu havayolu" sorusu hazır', priority: 'high' }
    ]
  },
  {
    id: 'practice',
    title: 'Pratik',
    emoji: '🎯',
    color: 'from-orange-500 to-red-600',
    items: [
      { id: 'p1', text: 'Kendinizi tanıtın cevabı ezber', priority: 'high' },
      { id: 'p2', text: 'En az 3 senaryo cevabı hazır', priority: 'high' },
      { id: 'p3', text: 'Ses kaydı ile pratik yapıldı', priority: 'medium' },
      { id: 'p4', text: 'Ayna karşısında beden dili çalışıldı', priority: 'medium' },
      { id: 'p5', text: 'İngilizce cevaplar pratik edildi', priority: 'high' },
      { id: 'p6', text: 'Grup aktivitesi senaryoları düşünüldü', priority: 'medium' },
      { id: 'p7', text: 'Zor sorulara cevaplar hazırlandı', priority: 'medium' }
    ]
  },
  {
    id: 'logistics',
    title: 'Lojistik',
    emoji: '📍',
    color: 'from-cyan-500 to-blue-600',
    items: [
      { id: 'l1', text: 'Mülakat yeri ve saati not edildi', priority: 'high' },
      { id: 'l2', text: 'Ulaşım planı yapıldı', priority: 'high' },
      { id: 'l3', text: 'En az 30 dk erken varış planlandı', priority: 'high' },
      { id: 'l4', text: 'Telefon numarası kaydedildi', priority: 'medium' },
      { id: 'l5', text: 'Yedek plan hazırlandı (trafik vb.)', priority: 'medium' },
      { id: 'l6', text: 'Kahvaltı/öğle yemeği planlandı', priority: 'low' },
      { id: 'l7', text: 'Su şişesi hazırlandı', priority: 'low' }
    ]
  },
  {
    id: 'mental',
    title: 'Mental Hazırlık',
    emoji: '🧘',
    color: 'from-pink-500 to-rose-600',
    items: [
      { id: 'm1', text: 'Gece öncesi erken yatıldı', priority: 'high' },
      { id: 'm2', text: 'Pozitif düşünce egzersizi yapıldı', priority: 'medium' },
      { id: 'm3', text: 'Derin nefes tekniği pratik edildi', priority: 'medium' },
      { id: 'm4', text: 'Başarı görselleştirmesi yapıldı', priority: 'medium' },
      { id: 'm5', text: 'Motivasyon konuşması dinlendi', priority: 'low' },
      { id: 'm6', text: 'Gülümseme pratiği yapıldı', priority: 'medium' },
      { id: 'm7', text: '"Ben hazırım" mantrası tekrarlandı', priority: 'low' }
    ]
  }
];

export default function InterviewChecklist({ onBack }) {
  const [checkedItems, setCheckedItems] = useState(() => {
    const saved = localStorage.getItem('interviewChecklist');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('interviewChecklist', JSON.stringify(checkedItems));
  }, [checkedItems]);

  const toggleItem = (itemId) => {
    if (checkedItems.includes(itemId)) {
      setCheckedItems(checkedItems.filter(id => id !== itemId));
    } else {
      setCheckedItems([...checkedItems, itemId]);
    }
  };

  const getTotalItems = () => {
    return checklistCategories.reduce((acc, cat) => acc + cat.items.length, 0);
  };

  const getProgress = () => {
    const total = getTotalItems();
    return Math.round((checkedItems.length / total) * 100);
  };

  const getCategoryProgress = (category) => {
    const checked = category.items.filter(item => checkedItems.includes(item.id)).length;
    return Math.round((checked / category.items.length) * 100);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  const resetChecklist = () => {
    if (window.confirm('Tüm işaretleri kaldırmak istediğinize emin misiniz?')) {
      setCheckedItems([]);
    }
  };

  const progress = getProgress();

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="bg-gradient-to-br from-green-600 to-emerald-600 px-4 pt-6 pb-8 rounded-b-[2rem]">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={onBack}
              className="flex items-center gap-2 text-white/80 hover:text-white"
            >
              <Home className="w-5 h-5" />
              <span className="font-medium">Pratik Merkezi</span>
            </button>
            
            <button 
              onClick={resetChecklist}
              className="text-white/80 hover:text-white text-sm"
            >
              Sıfırla
            </button>
          </div>

          <div className="text-center text-white mb-4">
            <CheckSquare className="w-12 h-12 mx-auto mb-2 text-yellow-300" />
            <h1 className="text-2xl font-bold">Hazırlık Kontrol Listesi</h1>
            <p className="text-emerald-100 text-sm mt-1">Mülakat öncesi yapılacaklar</p>
          </div>

          {/* Progress */}
          <Card className="bg-white/20 backdrop-blur border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">Genel İlerleme</span>
                <span className="text-white font-bold">{progress}%</span>
              </div>
              <div className="bg-white/30 rounded-full h-3">
                <div 
                  className="bg-white rounded-full h-3 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-emerald-100 text-sm mt-2 text-center">
                {checkedItems.length} / {getTotalItems()} madde tamamlandı
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 mt-6">
        {/* Achievement Banner */}
        {progress === 100 && (
          <Card className="mb-6 bg-gradient-to-r from-yellow-400 to-orange-500 border-0">
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-white">
                <Trophy className="w-6 h-6" />
                <span className="font-bold text-lg">Tebrikler! Mülakata hazırsın! 🎉</span>
                <Sparkles className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Categories */}
        <div className="space-y-4">
          {checklistCategories.map((category) => {
            const catProgress = getCategoryProgress(category);
            const isComplete = catProgress === 100;
            
            return (
              <Card key={category.id} className={isComplete ? 'ring-2 ring-green-500' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                        <span className="text-xl">{category.emoji}</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800">{category.title}</h3>
                        <p className="text-xs text-gray-500">{catProgress}% tamamlandı</p>
                      </div>
                    </div>
                    {isComplete && <CheckSquare className="w-6 h-6 text-green-500" />}
                  </div>

                  <Progress value={catProgress} className="h-1.5 mb-3" />

                  <div className="space-y-2">
                    {category.items.map((item) => {
                      const isChecked = checkedItems.includes(item.id);
                      return (
                        <button
                          key={item.id}
                          onClick={() => toggleItem(item.id)}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all ${
                            isChecked ? 'bg-green-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          {isChecked ? (
                            <CheckSquare className="w-5 h-5 text-green-600 flex-shrink-0" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                          <span className={`flex-1 text-left text-sm ${isChecked ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
                            {item.text}
                          </span>
                          <Star className={`w-4 h-4 ${getPriorityColor(item.priority)}`} />
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Priority Legend */}
        <Card className="mt-6 bg-gray-50">
          <CardContent className="p-4">
            <h4 className="font-semibold text-gray-800 mb-2">Öncelik Seviyeleri</h4>
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-red-500" />
                <span className="text-sm text-gray-600">Yüksek</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="text-sm text-gray-600">Orta</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-green-500" />
                <span className="text-sm text-gray-600">Düşük</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
